import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { STATUS_LABEL, STATUS_STYLES, formatDateTime } from "@/lib/options";
import { Plus, Ticket, Users, CalendarClock, CheckCircle2, Activity, AlertCircle, Wrench, Flag, Package, Settings as SettingsIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { FOLLOWUP_LABELS, FOLLOWUP_STYLES } from "@/components/tickets/FollowupPanel";
import { PART_STATUS_LABELS, PART_STATUS_STYLES, trackingUrl } from "@/components/tickets/PartsPanel";

const PartGroup = ({ title, items, emptyHint }) => (
  <div>
    <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">{title} ({items.length})</div>
    {items.length === 0 ? (
      <div className="text-xs text-slate-400 pb-2">{emptyHint}</div>
    ) : (
      <div className="divide-y divide-slate-100">
        {items.map((p) => {
          const overdue = p.eta && p.eta < new Date().toISOString().slice(0, 10) && ["needed", "ordered", "shipped"].includes(p.status);
          const tUrl = trackingUrl(p.tracking_number);
          return (
            <Link key={p.id} to={`/tickets/${p.ticket_id}`} className="flex items-center justify-between gap-2 py-2 hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors" data-testid={`dash-part-${p.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-900 truncate">{p.name}{p.company ? ` — ${p.company}` : ""}</div>
                <div className="text-xs text-slate-500 truncate">
                  {p.customer_name}{p.appliance_type ? ` · ${p.appliance_type}` : ""}
                  {p.tracking_number && tUrl && <> · <span className="text-blue-700 underline" onClick={(e) => { e.preventDefault(); e.stopPropagation(); window.open(tUrl, "_blank"); }}>track</span></>}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {p.eta && <span className={`text-xs font-medium ${overdue ? "text-red-700" : "text-slate-500"}`}>{overdue ? "Late · " : "ETA "}{p.eta}</span>}
                <Badge variant="outline" className={`${PART_STATUS_STYLES[p.status] || ""} rounded-full text-[10px] px-2 py-0`}>
                  {PART_STATUS_LABELS[p.status] || p.status}
                </Badge>
              </div>
            </Link>
          );
        })}
      </div>
    )}
  </div>
);
import { toast } from "sonner";

const KpiCard = ({ icon: Icon, label, value, accent = "blue", delay = 0, testId }) => (
  <Card
    className="border-slate-200 fade-in-up"
    style={{ animationDelay: `${delay}ms` }}
    data-testid={testId}
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500">{label}</div>
          <div className="text-3xl font-heading font-bold text-slate-900 mt-1">{value ?? "—"}</div>
        </div>
        <div className={`w-9 h-9 rounded-md flex items-center justify-center bg-${accent}-50 text-${accent}-700`}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [upcoming, setUpcoming] = useState([]);
  const [incomplete, setIncomplete] = useState([]);
  const [followups, setFollowups] = useState([]);
  const [partsOverview, setPartsOverview] = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, t, u, f, p] = await Promise.all([
        api.get("/stats/dashboard"),
        api.get("/tickets"),
        api.get("/tickets/schedule/upcoming"),
        api.get("/tickets/followups/list"),
        api.get("/parts/overview"),
      ]);
      setStats(s.data);
      setRecent(t.data.slice(0, 5));
      setUpcoming(u.data.slice(0, 5));
      setFollowups(f.data);
      setPartsOverview(p.data);
      // Incomplete = anything not completed or cancelled. Sort oldest scheduled/created first.
      const inc = t.data
        .filter((x) => x.status === "new" || x.status === "in_progress")
        .sort((a, b) => {
          const da = a.scheduled_at || a.created_at || "";
          const db = b.scheduled_at || b.created_at || "";
          return da.localeCompare(db);
        })
        .slice(0, 10);
      setIncomplete(inc);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="space-y-6" data-testid="dashboard-root">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">A quick view of your repair operation.</p>
        </div>
        <div className="flex items-center gap-2">
          {user?.role === "admin" && (
            <Link to="/settings">
              <Button variant="outline" size="icon" data-testid="dashboard-settings-btn" title="Settings">
                <SettingsIcon className="w-4 h-4" />
              </Button>
            </Link>
          )}
          <Link to="/tickets/new">
            <Button className="bg-blue-700 hover:bg-blue-800 gap-2" data-testid="dashboard-new-ticket">
              <Plus className="w-4 h-4" /> New Ticket
            </Button>
          </Link>
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold text-slate-900">Upcoming appointments</h2>
            <Link to="/schedule" className="text-xs text-blue-700 font-medium" data-testid="view-schedule">Open schedule</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {upcoming.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">No upcoming appointments.</div>
            )}
            {upcoming.map((t) => {
              const closed = ["completed", "cancelled", "declined", "expired"].includes(t.status);
              return (
              <Link
                key={t.id}
                to={`/tickets/${t.id}`}
                className={`flex items-center justify-between gap-2 py-3 hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors ${closed ? "opacity-60" : ""}`}
                data-testid={`upcoming-${t.id}`}
              >
                <div className="min-w-0">
                  <div className={`font-medium text-slate-900 truncate ${t.status === "completed" ? "line-through" : ""}`}>
                    {t.customer ? `${t.customer.first_name} ${t.customer.last_name}` : "Unknown"}
                  </div>
                  <div className="text-xs text-slate-500 truncate capitalize">{t.appliance_type} · {t.manufacturer}</div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {t.status !== "new" && (
                    <Badge variant="outline" className={`${STATUS_STYLES[t.status] || ""} rounded-full text-[10px] px-2 py-0`} data-testid={`upcoming-status-${t.id}`}>
                      {STATUS_LABEL[t.status] || t.status}
                    </Badge>
                  )}
                  <div className="text-xs text-slate-600 font-medium">{formatDateTime(t.scheduled_at)}</div>
                </div>
              </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-total" icon={Ticket} label="Total Tickets" value={stats?.total} accent="blue" delay={0} />
        <KpiCard testId="kpi-active" icon={Activity} label="In Progress" value={stats?.in_progress} accent="amber" delay={60} />
        <KpiCard testId="kpi-completed-today" icon={CheckCircle2} label="Completed Today" value={stats?.completed_today} accent="green" delay={120} />
        <KpiCard testId="kpi-scheduled-today" icon={CalendarClock} label="Scheduled Today" value={stats?.scheduled_today} accent="blue" delay={180} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <KpiCard testId="kpi-new" icon={AlertCircle} label="New (open)" value={stats?.new} accent="blue" delay={0} />
        <KpiCard testId="kpi-cancelled" icon={AlertCircle} label="Cancelled" value={stats?.cancelled} accent="red" delay={60} />
        <KpiCard testId="kpi-customers" icon={Users} label="Customers" value={stats?.customers} accent="blue" delay={120} />
      </div>

      {followups.length > 0 && (
        <Card className="border-amber-200" data-testid="followups-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Flag className="w-4 h-4 text-amber-600" />
              <h2 className="font-heading text-lg font-bold text-slate-900">Follow-ups</h2>
              <span className="text-xs text-slate-500">({followups.length})</span>
            </div>
            <div className="divide-y divide-slate-100">
              {followups.map((t) => {
                const fu = t.followup || {};
                const overdue = fu.due_date && fu.due_date < new Date().toISOString().slice(0, 10);
                return (
                  <Link
                    key={t.id}
                    to={`/tickets/${t.id}`}
                    className="flex items-center justify-between gap-2 py-3 hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors"
                    data-testid={`followup-row-${t.id}`}
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">
                        {t.customer ? `${t.customer.first_name} ${t.customer.last_name}` : "Unknown"}
                      </div>
                      <div className="text-xs text-slate-500 truncate">
                        {fu.note || <span className="capitalize">{t.appliance_type} · {t.manufacturer}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {fu.due_date && (
                        <span className={`text-xs font-medium ${overdue ? "text-red-700" : "text-slate-500"}`}>
                          {overdue ? "Overdue · " : "Due "}{fu.due_date}
                        </span>
                      )}
                      <Badge variant="outline" className={`${FOLLOWUP_STYLES[fu.type] || ""} rounded-full text-[10px] px-2 py-0`}>
                        {FOLLOWUP_LABELS[fu.type] || fu.type}
                      </Badge>
                    </div>
                  </Link>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="border-slate-200" data-testid="incomplete-jobs-card">        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Wrench className="w-4 h-4 text-amber-600" />
              <h2 className="font-heading text-lg font-bold text-slate-900">Incomplete jobs</h2>
              <span className="text-xs text-slate-500">
                ({incomplete.length}{incomplete.length === 10 ? "+" : ""} open)
              </span>
            </div>
            <Link
              to="/tickets?status=in_progress"
              className="text-xs text-blue-700 font-medium"
              data-testid="view-incomplete"
            >
              View all
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {incomplete.length === 0 && (
              <div className="text-sm text-slate-500 py-6 text-center">
                All caught up — no open jobs. 🎉
              </div>
            )}
            {incomplete.map((t) => {
              const hasParts = !!(t.parts_needed && t.parts_needed.trim());
              return (
                <Link
                  key={t.id}
                  to={`/tickets/${t.id}`}
                  className="flex items-center justify-between py-3 gap-3 hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors"
                  data-testid={`incomplete-${t.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium text-slate-900 truncate">
                        {t.customer ? `${t.customer.first_name} ${t.customer.last_name}` : "Unknown"}
                      </div>
                      {hasParts && (
                        <span
                          className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200 flex items-center gap-0.5"
                          title={`Parts needed: ${t.parts_needed}`}
                          data-testid={`parts-badge-${t.id}`}
                        >
                          Parts
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 truncate capitalize">
                      {t.appliance_type}
                      {t.manufacturer ? ` · ${t.manufacturer}` : ""}
                      {t.model_number ? ` · #${t.model_number}` : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-xs text-slate-500 hidden sm:block">
                      {t.scheduled_at ? formatDateTime(t.scheduled_at) : "Unscheduled"}
                    </div>
                    <Badge variant="outline" className={`${STATUS_STYLES[t.status]} text-xs rounded-full`}>
                      {STATUS_LABEL[t.status]}
                    </Badge>
                  </div>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {partsOverview && (partsOverview.to_pickup.length > 0 || partsOverview.on_order.length > 0 || partsOverview.arrived.length > 0) && (
        <Card className="border-blue-200" data-testid="parts-card">
          <CardContent className="p-5 space-y-4">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-blue-700" />
              <h2 className="font-heading text-lg font-bold text-slate-900">Parts</h2>
            </div>
            <PartGroup title="To pick up" items={partsOverview.to_pickup} emptyHint="Nothing to pick up." />
            <PartGroup title="On order" items={partsOverview.on_order} emptyHint="No parts on order." />
            <PartGroup title="Arrived — job still open" items={partsOverview.arrived} emptyHint="No arrived parts waiting." />
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-heading text-lg font-bold text-slate-900">Recent tickets</h2>
              <Link to="/tickets" className="text-xs text-blue-700 font-medium" data-testid="view-all-tickets">View all</Link>
            </div>
            <div className="divide-y divide-slate-100">
              {recent.length === 0 && (
                <div className="text-sm text-slate-500 py-6 text-center">No tickets yet. Create your first one.</div>
              )}
              {recent.map((t) => (
                <Link
                  key={t.id}
                  to={`/tickets/${t.id}`}
                  className="flex items-center justify-between py-3 hover:bg-slate-50 -mx-2 px-2 rounded-md transition-colors"
                  data-testid={`recent-ticket-${t.id}`}
                >
                  <div className="min-w-0">
                    <div className="font-medium text-slate-900 truncate">
                      {t.customer ? `${t.customer.first_name} ${t.customer.last_name}` : "Unknown"}
                    </div>
                    <div className="text-xs text-slate-500 truncate capitalize">{t.appliance_type} · {t.job_type}</div>
                  </div>
                  <Badge variant="outline" className={`${STATUS_STYLES[t.status]} text-xs rounded-full`}>
                    {STATUS_LABEL[t.status]}
                  </Badge>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
